class DocumentUploader extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("#document-uploader-form--main");
    this._onFormSubmit = this.onFormSubmit.bind(this);
    this._onFieldChange = this.onFieldChange.bind(this);
    this._onFieldBlur = this.onFieldBlur.bind(this);
    this._onEmailBlur = this.onEmailBlur.bind(this);
    this._onConsentChange = this.onConsentChange.bind(this);
    this._onSupportClick = this.onSupportClick.bind(this);
    this._onPageHide = this.onPageHide.bind(this);

    if (this.form) {
      this.formError = this.form.querySelector(".js--form-error");
      this.form.addEventListener("submit", this._onFormSubmit);
      this.form.querySelectorAll("[required]").forEach((field) => {
        field.addEventListener("change", this._onFieldChange);
      });
      // Email fields also validate on blur so an invalid address is flagged as
      // soon as the visitor leaves the field, rather than only on submit.
      this.form.querySelectorAll('input[type="email"]').forEach((field) => {
        field.addEventListener("blur", this._onFieldBlur);
      });

      // Analytics: email-entered (on blur, once a value is present) and
      // consent check/uncheck. Both push to the GTM dataLayer (see trackEvent).
      this.emailInput = this.form.querySelector('input[type="email"]');
      if (this.emailInput) this.emailInput.addEventListener("blur", this._onEmailBlur);

      this.consentInput = this.form.querySelector('input[name="consent"]');
      if (this.consentInput) this.consentInput.addEventListener("change", this._onConsentChange);
    }

    // Analytics: support-link clicks are caught with one delegated listener on the
    // component — the help box and the "information is protected" footer (which
    // holds the Privacy Policy link) live outside the form. Abandonment is tracked
    // on page exit before the confirmation screen renders.
    this.addEventListener("click", this._onSupportClick);
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("pagehide", this._onPageHide);
    }

    this.setupDropzones();

    this.uploadState = this.querySelector(".js--upload-state");
    this.uploadStateMessage = this.querySelector(".js--upload-state-progress-message");

    // Submission retry: the retry button is shown only on a retryable failure
    // (network/CORS or HTTP 5xx/429). Each click re-runs uploadDocument up to
    // maxRetries times; once exhausted the flow falls to the terminal
    // "contact Client Care" state. maxRetries is configurable via the markup
    // (data-max-retries), defaulting to 3.
    this.retryActions = this.querySelector("[data-upload-retry-actions]");
    this.retryButton = this.querySelector("[data-upload-retry]");
    this.maxRetries = parseInt(this.uploadState?.dataset.maxRetries, 10) || 3;
    this.retryCount = 0;
    if (this.retryButton) {
      this._onRetryClick = this.onRetryClick.bind(this);
      this.retryButton.addEventListener("click", this._onRetryClick);
    }

    // Confirmation (success) refs
    this.confirmation = this.querySelector("[data-confirmation]");
    this.confirmationHeading = this.querySelector("[data-confirmation-heading]");
    this.statusPanel = this.querySelector("[data-panel-id='panel-status']");
    this.introMessage = this.querySelector(".js--document-uploader-message");

    // OCR / verify-details refs
    this.verifyGroup = this.querySelector("[data-verify-group]");
    this.ocrProgress = this.querySelector("[data-ocr-progress]");
    this.ocrBar = this.querySelector("[data-ocr-bar]");
    this.ocrBarFill = this.querySelector("[data-ocr-bar-fill]");
    this.ocrLabel = this.querySelector("[data-ocr-label]");
    this.ocrExtractingLabel = this.ocrLabel ? this.ocrLabel.textContent : "";
    this.ocrErrorMessage = this.ocrProgress ? this.ocrProgress.dataset.messageError || "" : "";
    this.ocrExtractedMessage = this.ocrProgress ? this.ocrProgress.dataset.messageExtracted || "" : "";
    // Visually-hidden polite live region for OCR start/completion announcements.
    this.ocrStatus = this.querySelector("[data-ocr-status]");
    this.rawToggle = this.querySelector("[data-raw-toggle]");
    this.rawTextBox = this.querySelector("[data-raw-text]");
    this.submitButton = this.querySelector(".js--document-uploader-submit");

    this.ocrLang = this.resolveOcrLang();
    this.uploadFile = null; // EXIF-stripped / HEIC-converted file, used for the eventual upload
    this.rawText = "";
    this.isExtracting = false;
    // Analytics: how many documents the visitor has attached this session. Bumped
    // on each valid file selection and reported as attempt_number when a document
    // is removed, so the funnel shows which attempt was discarded.
    this._uploadAttempt = 0;
    // Analytics: guards for the abandonment event — _completed suppresses it once
    // the confirmation renders (a completed flow isn't abandoned); _abandonTracked
    // ensures it fires at most once per session.
    this._completed = false;
    this._abandonTracked = false;
    this._libsPromise = null;
    // Cancellation state for in-flight OCR. `_extractionToken` identifies the
    // current run: bumping it supersedes any run still awaiting, so its late
    // results are discarded. `_activeWorkers` holds the live Tesseract workers so
    // a cancel can terminate them mid-job (see cancelExtraction).
    this._extractionToken = 0;
    this._activeWorkers = new Set();
    // Freeze/timeout guards that unstick a stuck extraction (otherwise a hung
    // worker leaves isExtracting true and submit disabled forever). Two layers:
    //   _ocrStallMs   — the stall watchdog fails a run whose OCR progress has not
    //                   advanced for this long (a real freeze: a hung WASM worker
    //                   stops emitting progress). Distinguishes "frozen" from
    //                   "slow but working", which keeps bumping progress.
    //   _ocrTimeoutMs — a hard per-file backstop covering the phases before
    //                   progress starts (createWorker, HEIC decode, PDF render)
    //                   and any freeze the stall check misses.
    // Both are tunable via data-* so an environment can loosen them for large
    // documents without a code change. _ocrStallCheckMs is the watchdog cadence.
    this._ocrTimeoutMs = parseInt(this.dataset.ocrTimeoutMs, 10) || 90000;
    this._ocrStallMs = parseInt(this.dataset.ocrStallMs, 10) || 20000;
    this._ocrStallCheckMs = 2000;
    // Backstop for the upload POST. A browser will not time out an
    // established-but-silent connection (a server that accepts the body then
    // never responds, a hung load balancer), so without this the "verifying"
    // state spins forever with no error and no retry. On expiry the fetch is
    // aborted and surfaces as a retryable failure. Tunable via data-upload-timeout-ms.
    this._uploadTimeoutMs = parseInt(this.dataset.uploadTimeoutMs, 10) || 30000;
    // Backstop for a stalled OCR-library request. A <script> whose request hangs
    // fires neither onload nor onerror, which would otherwise leave extraction
    // hung with submit disabled forever (this load happens before the per-file
    // withTimeout, so that backstop does not cover it). Tunable via data-lib-timeout-ms.
    this._libLoadTimeoutMs = parseInt(this.dataset.libTimeoutMs, 10) || 30000;

    if (this.rawToggle) {
      this.rawToggle.addEventListener("click", () => this.toggleRawText());
    }
  }

  // Resolves the Tesseract traineddata code for the current session. Auto-detects
  // from the storefront language (the <html lang> attribute, set by Shopify from
  // request.locale.iso_code — e.g. "en-CA" / "fr-CA") so a French visitor OCRs with
  // the French model. An explicit data-ocr-lang overrides the detection; pass "auto"
  // (or omit it) to detect. Falls back to English for any non-French locale.
  resolveOcrLang() {
    const override = this.dataset.ocrLang;
    if (override && override !== "auto") return override;

    const htmlLang = (document.documentElement.lang || "").toLowerCase();
    return htmlLang.startsWith("fr") ? "fra" : "eng";
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this._onFormSubmit);
      this.form.querySelectorAll("[required]").forEach((field) => {
        field.removeEventListener("change", this._onFieldChange);
      });
      this.form.querySelectorAll('input[type="email"]').forEach((field) => {
        field.removeEventListener("blur", this._onFieldBlur);
      });
    }
    if (this.emailInput && this._onEmailBlur) {
      this.emailInput.removeEventListener("blur", this._onEmailBlur);
    }
    if (this.consentInput && this._onConsentChange) {
      this.consentInput.removeEventListener("change", this._onConsentChange);
    }
    if (this._onSupportClick) {
      this.removeEventListener("click", this._onSupportClick);
    }
    if (this._onPageHide && typeof window !== "undefined" && window.removeEventListener) {
      window.removeEventListener("pagehide", this._onPageHide);
    }
    if (this.retryButton && this._onRetryClick) {
      this.retryButton.removeEventListener("click", this._onRetryClick);
    }
  }

  // Wire up each drag-and-drop upload zone: browse button, file selection,
  // remove button, and the drag/drop events.
  setupDropzones() {
    this.querySelectorAll("[data-dropzone]").forEach((zone) => {
      const input = zone.querySelector("input[type='file']");
      const browse = zone.querySelector("[data-dropzone-browse]");
      const camera = zone.querySelector("[data-dropzone-camera]");
      const remove = zone.querySelector("[data-dropzone-remove]");

      if (browse && input) {
        browse.addEventListener("click", () => {
          input.removeAttribute("capture");
          input.click();
        });
      }

      // Take a photo (mobile only): reuse the same file input but hint the
      // browser to open the rear-facing camera via the capture attribute.
      if (camera && input) {
        camera.addEventListener("click", () => {
          input.setAttribute("capture", "environment");
          input.click();
        });
      }

      if (input) {
        input.addEventListener("change", () => {
          this.handleFileInput(zone);
        });
      }

      if (remove && input) {
        remove.addEventListener("click", () => {
          // Analytics: the veteran is discarding an attached document to replace it.
          // Classify why from the current state (before we reset it below).
          this.trackEvent("vac_upload_document_removed", {
            reason: this.removalReason(),
            attempt_number: this._uploadAttempt,
          });
          this.cancelExtraction();
          input.value = "";
          this.updateDropzone(zone);
          this.clearFieldError(input);
          this.resetVerify();
        });
      }

      ["dragenter", "dragover"].forEach((eventName) => {
        zone.addEventListener(eventName, (e) => {
          e.preventDefault();
          zone.classList.add("is-dragover");
        });
      });

      ["dragleave", "dragend"].forEach((eventName) => {
        zone.addEventListener(eventName, (e) => {
          e.preventDefault();
          zone.classList.remove("is-dragover");
        });
      });

      zone.addEventListener("drop", (e) => {
        e.preventDefault();
        zone.classList.remove("is-dragover");
        if (input && e.dataTransfer.files.length) {
          input.files = e.dataTransfer.files;
          this.handleFileInput(zone);
        }
      });
    });
  }

  // Runs whenever a file is chosen via drop or the browse dialog. Validates the
  // file's type/size up front so unsupported files are rejected immediately (the
  // accept attribute only filters the browse dialog, never drops), then proceeds
  // to OCR only for a valid file.
  handleFileInput(zone) {
    const input = zone.querySelector("input[type='file']");
    if (!input) return;

    if (input.files && input.files.length) {
      const file = input.files[0];
      const error = this.getFileError(input, file);
      if (error) {
        // Analytics: the file was rejected up front (wrong format or over size).
        this.trackEvent("vac_upload_file_error", { error_type: this.classifyFileError(input, file) });
        input.value = "";
        this.updateDropzone(zone);
        this.resetVerify();
        this.setFieldError(input, error);
        return;
      }
    }

    this.clearFieldError(input);
    this.updateDropzone(zone);

    // Analytics: a valid file was dropped/selected in the upload zone.
    if (input.files && input.files.length) {
      const file = input.files[0];
      this._uploadAttempt += 1;
      this.trackEvent("vac_upload_file_selected", {
        file_type: this.fileTypeLabel(file),
        file_size_kb: this.fileSizeKb(file),
      });
    }

    this.handleFileSelected(zone);
  }

  updateDropzone(zone) {
    const input = zone.querySelector("input[type='file']");
    const prompt = zone.querySelector("[data-dropzone-prompt]");
    const selected = zone.querySelector("[data-dropzone-selected]");
    const filename = zone.querySelector("[data-filename]");
    const meta = zone.querySelector("[data-file-meta]");
    const hasFile = !!(input && input.files && input.files.length);

    if (hasFile) {
      const file = input.files[0];
      if (filename) filename.textContent = file.name;
      if (meta) meta.textContent = this.formatFileMeta(file);
    }

    if (prompt) prompt.hidden = hasFile;
    if (selected) selected.hidden = !hasFile;
    zone.classList.toggle("has-file", hasFile);
  }

  // Builds the "PDF · 1.2 MB" summary shown next to a selected file's name.
  formatFileMeta(file) {
    const extension = (file.name.split(".").pop() || "").toUpperCase();
    return `${extension} · ${this.formatFileSize(file.size)}`;
  }

  formatFileSize(bytes) {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
  }

  // ── Analytics ────────────────────────────────────────────────────────────
  // VAC upload funnel events are pushed to the Google Tag Manager dataLayer
  // (GTM container wired in snippets/ga-gtm-tags.liquid). Each push carries an
  // `event` name plus any parameters. Guarded so it is a safe no-op if GTM
  // isn't present (dataLayer is created lazily on first push, matching GTM's
  // own snippet).
  trackEvent(event, params = {}) {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
  }

  // Short file-type label for analytics: normalizes to pdf / jpg / png (jpeg → jpg),
  // reports heic for HEIC/HEIF, and otherwise falls back to the MIME subtype or
  // extension. Derived from the MIME type first, then the filename extension.
  fileTypeLabel(file) {
    if (!file) return "";
    if (this.isHeic(file)) return "heic";
    const subtype = (file.type.split("/")[1] || "").toLowerCase();
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const label = subtype || ext;
    return label === "jpeg" ? "jpg" : label;
  }

  fileSizeKb(file) {
    return file ? Math.round(file.size / 1024) : 0;
  }

  // Classifies an up-front file rejection for analytics, mirroring getFileError's
  // precedence (type is checked before size): "invalid_file_type" or
  // "file_too_large" (or "" when the file is acceptable).
  classifyFileError(field, file) {
    const acceptedTypes = (field.accept || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (acceptedTypes.length && !this.isTypeAccepted(file, acceptedTypes)) return "invalid_file_type";
    if (this.getSizeError(field, file.size)) return "file_too_large";
    return "";
  }

  // Why the veteran is removing an attached document, inferred from the current
  // UI state (read before the remove handler resets it):
  //   ocr_incorrect — OCR succeeded and the verify fields are showing (they judge
  //                   the read/document wrong and want to re-upload).
  //   ocr_failed    — an OCR/processing error is currently surfaced on the field.
  //   wrong_file    — a file is attached but was neither read nor errored (e.g. a
  //                   non-OCR file type, or a removal before extraction resolves).
  removalReason() {
    if (this.verifyGroup && !this.verifyGroup.hidden) return "ocr_incorrect";
    const uploadField = this.querySelector(".document-uploader__upload");
    if (uploadField && uploadField.classList && uploadField.classList.contains("form__field--error")) return "ocr_failed";
    return "wrong_file";
  }

  // Delegated support-link handler. Scoped to the help box and the "information is
  // protected" footer (which holds the Privacy Policy link) so it never fires for
  // in-form anchors (e.g. the success-CTA button). Only classifiable links track.
  onSupportClick(e) {
    const anchor = e.target.closest && e.target.closest("a");
    if (!anchor) return;
    if (!anchor.closest(".document-uploader__help, .document-uploader__protected")) return;
    const label = this.supportLinkLabel(anchor);
    if (label) this.trackEvent("vac_upload_support_clicked", { link_label: label });
  }

  // Classifies a support link into one of the tracked labels. tel:/mailto: schemes
  // identify the Client Care phone/email; the FAQ (added later) and Privacy Policy
  // are matched by href or link text. Returns "" for anything unrecognized so it is
  // not tracked.
  supportLinkLabel(anchor) {
    const href = (anchor.getAttribute("href") || "").toLowerCase();
    const text = (anchor.textContent || "").toLowerCase();
    if (href.startsWith("tel:")) return "client_care_phone";
    if (href.startsWith("mailto:")) return "client_care_email";
    if (href.includes("privacy") || text.includes("privacy")) return "privacy_policy";
    if (href.includes("faq") || text.includes("faq")) return "faq";
    return "";
  }

  // Fired on page exit (pagehide). Reports how far the visitor got before leaving,
  // unless the flow already completed. Fires at most once per session.
  onPageHide() {
    if (this._completed || this._abandonTracked) return;
    this._abandonTracked = true;
    this.trackEvent("vac_upload_abandoned", { abandonment_point: this.abandonmentPoint() });
  }

  // The furthest stage the visitor reached, checked from latest to earliest:
  //   after_consent — consent ticked (but not submitted/completed)
  //   after_ocr     — OCR succeeded and the verify fields are displayed
  //   during_ocr    — extraction still running
  //   before_file   — email entered but no successful document yet
  //   before_email  — nothing entered
  abandonmentPoint() {
    if (this.consentInput && this.consentInput.checked) return "after_consent";
    if (this.verifyGroup && !this.verifyGroup.hidden) return "after_ocr";
    if (this.isExtracting) return "during_ocr";
    const email = this.emailInput ? this.emailInput.value.trim() : "";
    return email ? "before_file" : "before_email";
  }

  onFormSubmit(e) {
    e.preventDefault();
    if (this.isExtracting) return; // wait for OCR to finish before allowing submit
    if (this.validateForm(this.form)) {
      this.submitForm();
    }
  }

  onFieldChange(e) {
    this.clearFieldError(e.target);
  }

  // Validates a field when the visitor leaves it. Only surfaces an error when the
  // field has content — an empty field on blur isn't flagged (the required check
  // is deferred to submit, so the user isn't scolded for a field they've merely
  // tabbed past). Wired only to email inputs, this catches an invalid address the
  // moment focus leaves rather than waiting for submit.
  onFieldBlur(e) {
    const field = e.target;
    if (field.value.trim()) {
      this.setFieldError(field, this.getFieldError(field));
    }
  }

  // Analytics: the visitor left the email field with a value entered. Fires on
  // blur only when a (trimmed) value is present, so tabbing through an empty
  // field is not counted.
  onEmailBlur() {
    const value = this.emailInput?.value.trim();
    if (value) this.trackEvent("vac_upload_email_entered");
  }

  // Analytics: the consent checkbox was toggled. action reflects the new state.
  onConsentChange() {
    this.trackEvent("vac_upload_consent_checked", {
      action: this.consentInput?.checked ? "check" : "uncheck",
    });
  }

  validateForm(form) {
    const fields = form.querySelectorAll("[required]");
    let valid = true;
    let firstInvalid = null;

    fields.forEach((field) => {
      const error = this.getFieldError(field);
      this.setFieldError(field, error);
      if (error) {
        valid = false;
        if (!firstInvalid) firstInvalid = field;
      }
    });

    if (firstInvalid) this.focusInvalidField(firstInvalid);

    return valid;
  }

  // Scrolls the first invalid field into view and moves focus to it so the user
  // isn't left wondering why submit did nothing. Scrolls the field's wrapper
  // (which carries the label + error message) rather than the bare input, and
  // guards focus for the file input, whose visually-hidden input can't take it —
  // its browse button gets focus instead.
  focusInvalidField(field) {
    const wrapper = field.closest(".form__field") || field;
    wrapper.scrollIntoView({ behavior: "smooth", block: "center" });

    const focusTarget = field.type === "file" ? wrapper.querySelector("[data-dropzone-browse]") || field : field;
    // preventScroll: the smooth scrollIntoView above owns the scroll; focus()'s
    // own instant jump would otherwise fight it.
    focusTarget.focus({ preventScroll: true });
  }

  // Scrolls the top of the section into view. Called after submit swaps to the
  // status/confirmation state so the user reads it from the top rather than
  // staying parked at the old submit button. scrollIntoView("start")
  // aligns to the raw viewport top, which tucks the heading under the sticky
  // header — so compute the target manually and subtract the header's height.
  scrollToTop() {
    // AuroraUtils maintains --theme-header-height on <html> (the sticky header's
    // rendered height), kept current on scroll/resize. Reuse it rather than
    // measuring the header ourselves.
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--theme-header-height"), 10) || 0;
    const top = this.getBoundingClientRect().top + window.scrollY - headerHeight;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  }

  getFieldError(field) {
    const wrapper = field.closest(".form__field");
    const requiredError = wrapper?.dataset.errorMessageRequired || "This field is required.";

    if (field.type === "checkbox") {
      return field.checked ? "" : requiredError;
    }

    if (field.type === "file") {
      if (!field.files.length) return requiredError;
      return this.getFileError(field, field.files[0]);
    }

    const value = field.value.trim();
    if (!value) return requiredError;

    // The form is novalidate, so the browser's native email check never runs —
    // validate the format ourselves for email inputs.
    if (field.type === "email" && !this.isValidEmail(value)) {
      return wrapper?.dataset.errorMessageEmail || "Please enter a valid email address.";
    }

    return "";
  }

  // Lightweight email-format check: a single @ with non-empty, whitespace-free
  // local and domain parts and at least one dot in the domain. Deliberately
  // permissive — the verifier endpoint is the source of truth; this only catches
  // obvious typos before submission.
  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  // Type/size validation for a chosen file (no required check — a file is present).
  // Returns the configured error message, or "" when the file is acceptable.
  getFileError(field, file) {
    const wrapper = field.closest(".form__field");
    const acceptedTypes = (field.accept || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const typeError = wrapper?.dataset.errorMessageFileType || "This file type is not supported.";

    if (acceptedTypes.length && !this.isTypeAccepted(file, acceptedTypes)) return typeError;
    return this.getSizeError(field, file.size);
  }

  // Byte-size check against the field's data-max-size. Returns the configured
  // error message, or "" when within limit. Shared by the initial selection
  // check (getFileError) and the post-re-encode re-check in handleFileSelected —
  // the canvas re-draw produces a fresh file whose size can exceed the source
  // (e.g. HEIC → JPEG, or a disk-optimized PNG re-encoded losslessly).
  getSizeError(field, sizeBytes) {
    const wrapper = field.closest(".form__field");
    const maxSizeMb = parseInt(field.dataset.maxSize, 10);
    if (maxSizeMb && sizeBytes > maxSizeMb * 1024 * 1024) {
      return wrapper?.dataset.errorMessageFileSize || `File exceeds the maximum size of ${maxSizeMb}MB.`;
    }
    return "";
  }

  // Whether the file matches one of the accepted MIME types. Some browsers report
  // an empty type for HEIC/HEIF, so fall back to the extension in that case.
  isTypeAccepted(file, acceptedTypes) {
    if (file.type && acceptedTypes.includes(file.type)) return true;
    if (this.isHeic(file) && (acceptedTypes.includes("image/heic") || acceptedTypes.includes("image/heif"))) return true;
    return false;
  }

  setFieldError(field, message) {
    const wrapper = field.closest(".form__field");
    if (!wrapper) return;

    const errorElement = wrapper.querySelector(".form__field-error-message");
    if (errorElement) {
      // Write into the dedicated text span when present (the upload field nests a
      // secondary OCR-error span in the same element) so setting the message never
      // wipes that sibling. Falls back to the element itself for plain fields.
      const textTarget = errorElement.querySelector("[data-error-text]") || errorElement;
      textTarget.textContent = message;
    }
    // The secondary OCR content is only relevant to the extraction error, so keep
    // it hidden for file type/size/required errors that share this banner.
    const secondary = wrapper.querySelector("[data-ocr-error-content]");
    if (secondary) secondary.hidden = true;
    wrapper.classList.toggle("form__field--error", message !== "");
    // Mark the control invalid for assistive tech (paired with the error message
    // referenced via aria-describedby). Cleared when the message is emptied.
    if (message !== "") field.setAttribute("aria-invalid", "true");
    else field.removeAttribute("aria-invalid");
  }

  clearFieldError(field) {
    this.setFieldError(field, "");
  }

  // ──────────────────────────────────────────────────────────────────────────
  // OCR pipeline
  //
  // Runs entirely client-side when a file is selected. Nothing is sent anywhere
  // during extraction. Flow:
  //   1. Lazy-load Tesseract.js / PDF.js / heic2any (first file only).
  //   2. Process the file:
  //        - Images: HEIC → JPEG (if needed), then EXIF-strip via canvas re-draw.
  //        - PDFs: rasterize each page to a canvas.
  //   3. OCR the resulting canvas(es) → raw text.
  //   4. Parse Name / K-Number / Expiry Date and populate the read-only verify
  //      fields, then reveal the "Verify details" group.
  // The processed (metadata-stripped) file is retained as this.uploadFile for the
  // eventual upload.
  // ──────────────────────────────────────────────────────────────────────────

  async handleFileSelected(zone) {
    const input = zone.querySelector("input[type='file']");
    if (!input || !input.files || !input.files.length) return;

    const file = input.files[0];

    // Only images and PDFs can be OCR'd. Anything else (e.g. a Word doc) is kept
    // as-is for upload and skips extraction.
    if (!this.isOcrSupported(file)) {
      this.uploadFile = file;
      return;
    }

    this.resetVerify();
    // Claim this run. If the file is removed (or another file is selected) while
    // we await below, the token moves on and this run bails at the next check
    // without touching the UI the cancel already reset.
    const token = ++this._extractionToken;
    this.isExtracting = true;
    this.setSubmitDisabled(true);
    this.setOcrProgress(0, this.ocrExtractingLabel);
    this.showOcrProgress(true);
    // Politely announce that extraction has begun (the progressbar itself is not a
    // live region, so its appearance would otherwise be silent to a screen reader).
    this.announceOcr(this.ocrExtractingLabel);

    // Analytics: OCR is starting for the attached file. Stamp the start time so
    // ocr_complete can report processing_time_ms on a successful extraction.
    this._ocrStartedAt = Date.now();
    this.trackEvent("vac_upload_ocr_started", {
      file_type: this.fileTypeLabel(file),
      file_size_kb: this.fileSizeKb(file),
    });

    try {
      await this.ensureOcrLibs();
      if (token !== this._extractionToken) return; // cancelled during lib load
      const { uploadFile, text } = await this.withTimeout(this.processFile(file), this._ocrTimeoutMs, "extraction");
      if (token !== this._extractionToken) return; // cancelled during OCR

      // The initial getFileError check sized the *source* file. The image path
      // re-encodes through a canvas (EXIF strip / HEIC → JPEG), so re-validate the
      // produced file — a source at or below the limit can exceed it once re-encoded.
      const sizeError = this.getSizeError(input, uploadFile.size);
      if (sizeError) {
        // Analytics: the re-encoded file grew past the size limit — a size rejection.
        this.trackEvent("vac_upload_file_error", { error_type: "file_too_large" });
        this.rejectFile(zone);
        this.setFieldError(input, sizeError);
        return;
      }

      this.rawText = text || "";
      if (this.rawTextBox) this.rawTextBox.textContent = this.rawText || "(no text detected)";

      const fields = this.parseFields(this.rawText);

      // A "successful" OCR run can still fail to read the required details (blank
      // values). Treat that as a failure: reject the file so nothing incomplete is
      // retained in the upload field, and surface the extraction error.
      if (this.hasExtractionFailure(fields)) {
        // Analytics: OCR ran but couldn't read every required field. full_failure
        // when nothing was extracted, partial_extraction when some fields came back.
        const { mappedKeys, missing } = this.ocrFieldStatus(fields);
        this.trackEvent("vac_upload_ocr_failed", {
          error_type: missing.length === mappedKeys.length ? "full_failure" : "partial_extraction",
          missing_fields: missing.join(","),
        });
        this.rejectFile(zone);
        this.showOcrError();
        return;
      }

      this.uploadFile = uploadFile;
      this.populateVerifyFields(fields);
      this.showVerifyGroup(true);
      // Announce that the read succeeded and the verify fields are ready to review —
      // revealing the group is a silent DOM change on its own.
      this.announceOcr(this.ocrExtractedMessage);

      // Analytics: extraction succeeded and the verify fields are now displayed.
      // fields_extracted lists the OCR-populated keys; processing_time_ms is the
      // elapsed time since ocr_started.
      const fieldsExtracted = Object.keys(fields).filter((key) => fields[key] && String(fields[key]).trim());
      this.trackEvent("vac_upload_ocr_complete", {
        fields_extracted: fieldsExtracted.join(","),
        processing_time_ms: this._ocrStartedAt ? Date.now() - this._ocrStartedAt : 0,
      });
    } catch (err) {
      // A cancel terminates the worker, which rejects the in-flight recognize.
      // Swallow that here — the cancel already reset the UI and there is no error
      // to show the user.
      if (token !== this._extractionToken) return;
      console.error("[document-uploader] OCR extraction failed", err);
      // Analytics: extraction itself threw. The per-file backstop and the stall
      // watchdog both surface as a timeout ("timed out" / worker "terminated");
      // anything else is a full failure. Nothing was parsed, so every mapped field
      // is reported missing.
      const { mappedKeys } = this.ocrFieldStatus({});
      const timedOut = /timed out|terminated/i.test(err?.message || "");
      this.trackEvent("vac_upload_ocr_failed", {
        error_type: timedOut ? "timeout" : "full_failure",
        missing_fields: mappedKeys.join(","),
      });
      // Extraction threw (a real error, a stall, or the per-file timeout) — reject
      // the file and surface the generic processing error, not the wrong-document
      // copy the hasExtractionFailure branch above uses.
      this.rejectFile(zone);
      this.showOcrError("processing");
    } finally {
      // Only the current run resets the shared extracting/submit/progress state. A
      // superseded run leaves it as the cancel (or newer run) already set it.
      if (token === this._extractionToken) this.endExtraction();
    }
  }

  // Stops any in-flight OCR run. Bumps the extraction token so the awaiting
  // pipeline in handleFileSelected discards its result, and terminates the live
  // Tesseract worker(s) so the running recognize job halts immediately rather
  // than finishing its (multi-megabyte, multi-second) work in the background.
  // Safe to call when nothing is extracting.
  cancelExtraction() {
    if (!this.isExtracting) return;
    this._extractionToken++;
    this._activeWorkers.forEach((worker) => {
      // terminate() rejects the pending recognize(); handleFileSelected's catch
      // ignores it because the token has moved on.
      worker.terminate();
    });
    this._activeWorkers.clear();
    this.endExtraction();
  }

  // Clears the shared "an extraction is running" UI state: re-enables submit and
  // hides the progress bar. Shared by normal completion (handleFileSelected's
  // finally) and cancellation so the reset lives in one place.
  endExtraction() {
    this.isExtracting = false;
    this.setSubmitDisabled(false);
    this.showOcrProgress(false);
  }

  // Races `promise` against a timer as the hard per-file backstop. On timeout it
  // terminates any live Tesseract worker so the underlying job actually stops —
  // Promise.race abandons the loser but does not cancel it, so without the
  // terminate a frozen worker would keep chewing the canvas in the background.
  // It does NOT bump _extractionToken, so the rejection surfaces through
  // handleFileSelected's catch as a real extraction error (rejectFile +
  // showOcrError), unlike the silent supersede path a manual cancel takes.
  withTimeout(promise, ms, label) {
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        this._activeWorkers.forEach((worker) => worker.terminate());
        this._activeWorkers.clear();
        reject(new Error(`OCR ${label} timed out after ${ms}ms`));
      }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
  }

  isOcrSupported(file) {
    return file.type === "application/pdf" || file.type.startsWith("image/") || this.isHeic(file);
  }

  isHeic(file) {
    return file.type === "image/heic" || file.type === "image/heif" || /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name);
  }

  loadScript(src, timeoutMs) {
    const ms = timeoutMs || this._libLoadTimeoutMs || 30000;
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      // A stalled request fires neither onload nor onerror. Time it out and drop
      // the dangling <script> so the memoized libs promise rejects (surfacing as
      // a processing error) and a fresh attempt starts clean rather than matching
      // the dead element on the querySelector short-circuit above.
      const timer = setTimeout(() => {
        script.remove();
        reject(new Error(`Timed out loading ${src}`));
      }, ms);
      script.src = src;
      script.async = true;
      script.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timer);
        reject(new Error(`Failed to load ${src}`));
      };
      document.head.appendChild(script);
    });
  }

  // Resolves the self-hosted OCR library URLs from the section's data-lib-*
  // attributes (each populated with an asset_url in the Liquid). tesseractWorker,
  // tesseractCore and langPath are only consumed by Tesseract.createWorker; all
  // three, plus the {lang}.traineddata.gz files under langPath, must be uploaded
  // as theme assets for OCR to work.
  resolveOcrLibs() {
    const d = this.dataset;
    return {
      tesseract: d.libTesseract,
      pdfjs: d.libPdfjs,
      pdfjsWorker: d.libPdfjsWorker,
      heic2any: d.libHeic2any,
      tesseractWorker: d.libTesseractWorker,
      tesseractCore: d.libTesseractCore,
      langPath: d.libLangPath,
    };
  }

  // Loads all OCR libraries once and memoizes the promise.
  ensureOcrLibs() {
    if (this._libsPromise) return this._libsPromise;

    const libs = this.resolveOcrLibs();
    this._ocrLibs = libs;

    this._libsPromise = (async () => {
      await Promise.all([this.loadScript(libs.tesseract), this.loadScript(libs.pdfjs), this.loadScript(libs.heic2any)]);
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = libs.pdfjsWorker;
      }
    })().catch((err) => {
      // Don't cache a failed/timed-out load: clear the memo so the next file
      // selection re-attempts loading rather than getting this rejected promise
      // (and failing instantly) forever.
      this._libsPromise = null;
      throw err;
    });

    return this._libsPromise;
  }

  async processFile(file) {
    if (file.type === "application/pdf") {
      return this.processPdf(file);
    }
    return this.processImage(file);
  }

  // Images: convert HEIC → JPEG if needed, strip EXIF/GPS metadata via a canvas
  // re-draw, then OCR the canvas. The canvas-derived file is what gets uploaded,
  // so device/location metadata never leaves the browser.
  async processImage(file) {
    let working = file;

    if (this.isHeic(file)) {
      const converted = await window.heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
      const blob = Array.isArray(converted) ? converted[0] : converted;
      working = new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
    }

    const { canvas, cleanFile } = await this.redrawToCanvas(working);
    const text = await this.ocrCanvas(canvas);
    return { uploadFile: cleanFile, text };
  }

  // Re-draws an image through a canvas. This discards all metadata (EXIF/GPS/etc.)
  // while preserving the pixels. Format is kept for PNG/WebP, otherwise JPEG.
  async redrawToCanvas(file) {
    const outType = file.type === "image/png" ? "image/png" : file.type === "image/webp" ? "image/webp" : "image/jpeg";

    let width;
    let height;
    let source;

    if (typeof window.createImageBitmap === "function") {
      // imageOrientation "from-image" bakes EXIF orientation into the pixels so
      // the stripped copy is not rotated.
      const bitmap = await window.createImageBitmap(file, { imageOrientation: "from-image" });
      width = bitmap.width;
      height = bitmap.height;
      source = bitmap;
    } else {
      const img = await this.loadImageElement(file);
      width = img.naturalWidth;
      height = img.naturalHeight;
      source = img;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(source, 0, 0);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, outType, 0.92));
    const ext = outType === "image/png" ? ".png" : outType === "image/webp" ? ".webp" : ".jpg";
    const cleanFile = new File([blob], file.name.replace(/\.[^.]+$/, ext), { type: outType });

    return { canvas, cleanFile };
  }

  loadImageElement(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to decode image"));
      };
      img.src = url;
    });
  }

  async ocrCanvas(canvas) {
    return this.recognizeCancelable(canvas, (progress) => {
      this.setOcrProgress(progress, this.ocrExtractingLabel);
    });
  }

  // OCRs a canvas through a worker we keep a reference to (in _activeWorkers) so
  // cancelExtraction can terminate it mid-job. Tesseract.recognize() (the
  // convenience API) hides its worker, so it can't be aborted — hence the explicit
  // createWorker/terminate here. onProgress receives the 0–1 recognition fraction.
  async recognizeCancelable(canvas, onProgress) {
    const libs = this._ocrLibs || {};
    // lastProgressAt drives the stall watchdog below. It stays undefined until we
    // baseline it after createWorker resolves: the "recognizing text" guard
    // ignores the init-phase logger events, so a hang while loading the WASM/
    // traineddata is left to the per-file backstop (withTimeout), not this check.
    let lastProgressAt;
    // Point Tesseract at the self-hosted worker, core WASM and traineddata so it
    // never falls back to its own CDN for those sub-resources.
    const worker = await window.Tesseract.createWorker(this.ocrLang, 1, {
      workerPath: libs.tesseractWorker,
      corePath: libs.tesseractCore,
      langPath: libs.langPath,
      logger: (m) => {
        if (m.status === "recognizing text") {
          lastProgressAt = Date.now();
          onProgress(m.progress);
        }
      },
    });
    this._activeWorkers.add(worker);

    // Stall watchdog: a frozen worker stops emitting progress. If none arrives
    // for _ocrStallMs, terminate it — that rejects the pending recognize() below,
    // which propagates as an extraction failure. Baselined here (not at the first
    // progress event) so a hang that starts before recognition is caught too.
    lastProgressAt = Date.now();
    const watchdog = setInterval(() => {
      if (Date.now() - lastProgressAt > this._ocrStallMs && this._activeWorkers.has(worker)) {
        this._activeWorkers.delete(worker);
        worker.terminate();
      }
    }, this._ocrStallCheckMs);

    try {
      const result = await worker.recognize(canvas);
      return result.data.text;
    } finally {
      clearInterval(watchdog);
      // A cancel/stall/backstop terminates + removes the worker itself; only tear
      // it down here when it wasn't already, to avoid a double terminate.
      if (this._activeWorkers.has(worker)) {
        this._activeWorkers.delete(worker);
        worker.terminate();
      }
    }
  }

  // PDFs are rasterized page-by-page (scale 2 for legible OCR) then OCR'd. The
  // original PDF is uploaded as-is — PDFs do not carry the camera EXIF/GPS
  // metadata the canvas re-draw is meant to remove.
  async processPdf(file) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = "";

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 2 });

      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;

      const pageText = await this.recognizeCancelable(canvas, (progress) => {
        const pageProgress = (pageNum - 1 + progress) / pdf.numPages;
        this.setOcrProgress(pageProgress, this.ocrExtractingLabel);
      });

      fullText += pageText + "\n";
    }

    return { uploadFile: file, text: fullText };
  }

  // ── Field parsing ──────────────────────────────────────────────────────────
  // Tuned to the VAC sample document's labels and formats (EN / FR-CA):
  //   Client Name:      / Nom du client :      John Smith
  //   File Number:      / Numéro de dossier :  K1111111       (K-prefix + digits)
  //   Expiration Date:  / Date d'expiration :  2026-07-10     (ISO YYYY-MM-DD)
  // Each parser keeps looser fallbacks so an OCR misread of the label (or a minor
  // layout variation) still has a chance of extracting the value.
  parseFields(text) {
    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    return {
      name: this.parseName(lines, text),
      k_number: this.parseKNumber(lines, text),
      expiry_date: this.parseExpiry(lines, text),
    };
  }

  // Labels: EN "Client Name:" / FR "Nom du client :". Also accepts common
  // alternates so an OCR misread of the exact label still resolves. The FR ":" is
  // preceded by a space (French typography), absorbed by \s* before the separator.
  // Captures to the end of the line.
  parseName(lines, text) {
    const labeled = text.match(/^\s*(?:client\s*name|full\s*name|member\s*name|name|nom\s*(?:du\s*client|complet)?)\s*[:#-]\s*(.+)$/im);
    if (labeled) return labeled[1].trim().replace(/\s{2,}/g, " ");
    return "";
  }

  // Labels: EN "File Number:" / FR "Numéro de dossier :", value is K-prefixed (e.g.
  // K1111111). The labeled pattern accepts either language (accent optional, since
  // eng-OCR often drops it); the bare fallback catches a K-prefixed run of digits
  // anywhere in the text regardless of label.
  parseKNumber(lines, text) {
    const labeled = text.match(/(?:file\s*(?:number|no|#)|num[eéÉ]ro\s*de\s*dossier|k[\s-]*(?:number|no|#))[^\dA-Za-z]{0,4}(K?[\d\s-]{5,})/i);
    if (labeled) return this.normalizeKNumber(labeled[1]);

    const bare = text.match(/\bK\s?-?\s?(\d{5,9})\b/i);
    return bare ? "K" + bare[1] : "";
  }

  normalizeKNumber(value) {
    const digits = (value.match(/\d/g) || []).join("");
    return digits ? "K" + digits : "";
  }

  // Labels: EN "Expiration Date:" / FR "Date d'expiration :", value is ISO
  // YYYY-MM-DD. Both languages anchor on the shared keyword "expiration", so the FR
  // label matches without a dedicated pattern. Tolerates an optional "date" word
  // after the keyword and OCR-mangled separators, then prefers an ISO match. The
  // generic DD/MM fallback is a last resort for a non-ISO layout and cannot
  // disambiguate DD vs MM when both are ≤ 12. Long-form dates (e.g. FR
  // "10 juillet 2026") are NOT parsed — add a month-name pattern if they appear.
  parseExpiry(lines, text) {
    const labeled = text.match(
      /(?:expiry|expires?|expiration|valid\s*(?:until|to|thru)|exp)(?:\s*date)?\b[^\dA-Za-z]{0,10}(\d{1,4}[/\-.]\d{1,2}[/\-.]\d{1,4})/i
    );
    if (labeled) return labeled[1];

    const iso = text.match(/\b(\d{4}[/\-.]\d{1,2}[/\-.]\d{1,2})\b/);
    if (iso) return iso[1];

    const generic = text.match(/\b(\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4})\b/);
    return generic ? generic[1] : "";
  }

  populateVerifyFields(fields) {
    this.querySelectorAll("[data-ocr-field]").forEach((input) => {
      const key = input.dataset.ocrField;
      if (Object.prototype.hasOwnProperty.call(fields, key)) {
        input.value = fields[key] || "";
      }
    });
  }

  // Inspects the OCR-mapped fields for a parsed result: which keys the form maps
  // (mappedKeys) and which of those came back empty/blank (missing). Shared by the
  // extraction-failure check and the ocr_failed analytics classification.
  ocrFieldStatus(fields) {
    const mappedKeys = [];
    this.querySelectorAll("[data-ocr-field]").forEach((input) => {
      const key = input.dataset.ocrField;
      if (key && !mappedKeys.includes(key)) mappedKeys.push(key);
    });

    const missing = mappedKeys.filter((key) => {
      const value = fields[key];
      return !value || !String(value).trim();
    });

    return { mappedKeys, missing };
  }

  // Extraction is considered failed when any OCR-mapped field in the form came back
  // empty — a partial read is not good enough to prefill the verify details. Returns
  // false when nothing is mapped (there is nothing to extract).
  hasExtractionFailure(fields) {
    const { mappedKeys, missing } = this.ocrFieldStatus(fields);
    if (mappedKeys.length === 0) return false;
    return missing.length > 0;
  }

  // Clears the selected file from the upload field and reverts the dropzone to its
  // empty prompt. Used when extraction fails so an incomplete document is never left
  // in the field. Does not clear the error banner — the caller shows it afterwards.
  rejectFile(zone) {
    const input = zone.querySelector("input[type='file']");
    if (input) input.value = "";
    this.uploadFile = null;
    this.updateDropzone(zone);
    this.showVerifyGroup(false);
  }

  // ── Verify / progress UI ─────────────────────────────────────────────────
  showVerifyGroup(show) {
    if (this.verifyGroup) this.verifyGroup.hidden = !show;
  }

  showOcrProgress(show) {
    if (this.ocrProgress) this.ocrProgress.hidden = !show;
    if (!show && this.ocrBarFill) this.ocrBarFill.style.width = "0%";
    if (!show && this.ocrBar) this.ocrBar.setAttribute("aria-valuenow", "0");
  }

  // Writes a discrete message into the polite live region so a screen reader
  // announces the OCR phase transitions (started / details ready). The per-percent
  // progress is left to the progressbar's aria-valuenow — narrating every tick here
  // would be far too chatty. Guarded so it is a no-op when the region is absent.
  announceOcr(message) {
    if (this.ocrStatus) this.ocrStatus.textContent = message || "";
  }

  setOcrProgress(fraction, label) {
    const pct = Math.min(100, Math.round(fraction * 100));
    if (this.ocrBarFill) this.ocrBarFill.style.width = pct + "%";
    if (this.ocrBar) this.ocrBar.setAttribute("aria-valuenow", String(pct));
    if (this.ocrLabel && label) this.ocrLabel.textContent = `${label} (${pct}%)`;
  }

  // Surfaces an OCR failure in the upload field's error banner (below the dropzone
  // icon), matching the file type/size errors. Falls back to the global form error
  // if the upload field isn't present. Two kinds:
  //   "extraction" (default) — the document read but the required details were
  //     missing (wrong/unreadable document). Uses the VAC-specific copy and reveals
  //     the secondary guidance content ("upload a clearer copy") beneath it.
  //   "processing" — extraction itself failed to run (a thrown error, a stall, or
  //     the per-file timeout). Uses the generic upload-error copy and keeps the
  //     VAC guidance hidden, since that message is self-contained.
  showOcrError(kind = "extraction") {
    this.showVerifyGroup(false);
    // The error is announced by the role="alert" message below; clear the polite
    // region so a stale "Reading your document…" isn't left for a screen reader.
    this.announceOcr("");
    const isProcessing = kind === "processing";

    const wrapper = this.querySelector(".document-uploader__upload");
    const errorElement = wrapper?.querySelector(".form__field-error-message");
    if (!wrapper || !errorElement) {
      const fallback = isProcessing ? wrapper?.dataset.errorMessageProcessing || this.ocrErrorMessage : this.ocrErrorMessage;
      if (fallback) this.showFormError(fallback);
      return;
    }

    const message = isProcessing
      ? wrapper.dataset.errorMessageProcessing || this.ocrErrorMessage
      : wrapper.dataset.errorMessageOcr || this.ocrErrorMessage;
    const textTarget = errorElement.querySelector("[data-error-text]") || errorElement;
    textTarget.textContent = message;

    const secondary = wrapper.querySelector("[data-ocr-error-content]");
    if (secondary) secondary.hidden = isProcessing;

    wrapper.classList.add("form__field--error");
  }

  // Clears everything OCR produced. Called when the file is removed.
  resetVerify() {
    this.uploadFile = null;
    this.rawText = "";
    this.showVerifyGroup(false);
    this.showOcrProgress(false);
    this.announceOcr("");
    this.clearFormError();
    // Clear the upload banner (OCR error message + secondary content).
    const fileInput = this.querySelector(".document-uploader__file-input");
    if (fileInput) this.clearFieldError(fileInput);
    this.querySelectorAll("[data-ocr-field]").forEach((input) => {
      input.value = "";
    });
    if (this.rawTextBox) {
      this.rawTextBox.textContent = "";
      this.rawTextBox.hidden = true;
    }
    if (this.rawToggle) {
      this.rawToggle.textContent = this.rawToggle.dataset.labelShow || this.rawToggle.textContent;
    }
  }

  toggleRawText() {
    if (!this.rawTextBox || !this.rawToggle) return;
    const willShow = this.rawTextBox.hidden;
    this.rawTextBox.hidden = !willShow;
    this.rawToggle.textContent = willShow ? this.rawToggle.dataset.labelHide : this.rawToggle.dataset.labelShow;
  }

  setSubmitDisabled(disabled) {
    if (this.submitButton) this.submitButton.disabled = disabled;
  }

  // The EXIF-stripped / HEIC-converted file produced during OCR. Falls back to the
  // original selected file when OCR did not run (e.g. an unsupported file type).
  getUploadFile() {
    if (this.uploadFile) return this.uploadFile;
    const input = this.form?.querySelector("input[type='file']");
    return input && input.files && input.files.length ? input.files[0] : null;
  }

  // Assembles the multipart body POSTed to the verify endpoint: the processed
  // (EXIF-stripped) "file" part, the visitor's "email", and their "consent"
  // decision. clientId and documentType travel in the URL. Consent is sent as a
  // "true"/"false" string reflecting the checkbox's checked state so the grant is
  // recorded alongside the document (required-field validation has already run, so
  // it is "true" on a normal submit, but the actual state is sent regardless).
  buildSubmitFormData() {
    const formData = new FormData();
    const file = this.getUploadFile();
    if (file) formData.append("file", file);

    const email = this.form?.querySelector('[name="email"]')?.value.trim();
    if (email) formData.append("email", email);

    const consent = this.form?.querySelector('[name="consent"]');
    if (consent) formData.append("consent", consent.checked ? "true" : "false");

    return formData;
  }

  // Submit lifecycle: a single submission to the verify endpoint on the status
  // panel. Validation (required fields) has already passed in onFormSubmit.
  submitForm() {
    this.clearFormError();
    // Analytics: Submit clicked with all conditions met (validation has passed
    // in onFormSubmit before this runs).
    this.trackEvent("vac_upload_submitted");
    // Fresh submission — reset the retry budget. Retries themselves go through
    // onRetryClick (which does NOT reset it) so the budget spans the attempt.
    this.retryCount = 0;
    this.uploadDocument();
  }

  // Manual retry: the visitor clicked "Try again" on a retryable failure. Spend
  // one retry from the budget and re-run the submission.
  onRetryClick() {
    this.retryCount += 1;
    this.uploadDocument();
  }

  // The client identifier for the upload URL is the K-number extracted from the
  // document (the field mapped to the "k_number" OCR value, e.g. K1111111). Read
  // by OCR mapping so it is independent of the configurable field_key.
  getClientId() {
    const field = this.querySelector('[data-ocr-field="k_number"]') || this.form?.querySelector('[name="k_number"]');
    return field ? field.value.trim() : "";
  }

  // Fills the {clientId} / {documentType} placeholders in the configured verify
  // endpoint. clientId is the extracted K-number (path segment, percent-encoded);
  // documentType is the section's document type setting (query param, spaces as "+"
  // to match the API's form, e.g. documentType=MBC+Authorization+Form).
  buildUploadUrl(template) {
    return template
      .replace("{clientId}", encodeURIComponent(this.getClientId()))
      .replace("{documentType}", encodeURIComponent(this.dataset.documentType || "").replace(/%20/g, "+"));
  }

  async uploadDocument() {
    const template = this.dataset.verifyEndpoint;

    this.setPanel("panel-status");
    this.setUploadState("verifying");
    this.scrollToTop();

    // No endpoint configured → there is nowhere to submit. Surface the same
    // client-facing error as a failed submission rather than proceeding. Not
    // retryable — a resend can't fix a missing configuration.
    if (!template) {
      console.error("[document-uploader] no verify endpoint configured — cannot submit");
      this.setUploadState("error-final");
      return;
    }

    // The K-number populates the {clientId} path segment, so it must be present.
    // (It comes from OCR, which must have succeeded to reach submit, but guard anyway.)
    // Not retryable — the missing input won't change on an identical resend.
    const clientId = this.getClientId();
    if (!clientId) {
      console.error("[document-uploader] no K-number extracted — cannot build upload URL, aborting submit");
      this.setUploadState("error-final");
      return;
    }

    const url = this.buildUploadUrl(template);

    // Abort a stalled upload after _uploadTimeoutMs: a browser won't time out an
    // established-but-silent connection on its own, so without this the "verifying"
    // state spins forever. The abort rejects the fetch below and is routed as a
    // retryable failure (a timeout is transient, like any network error).
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this._uploadTimeoutMs);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: this.buildSubmitFormData(),
        signal: controller.signal,
        // No client-side auth — the endpoint accepts an unauthenticated POST.
        // Do NOT set Content-Type: the browser sets the multipart boundary for FormData.
      });

      if (!response.ok) {
        // Read the response body for the server's error detail (best effort).
        const detail = await response.text().catch(() => "");
        console.error(`[document-uploader] upload failed — HTTP ${response.status} ${response.statusText}`, detail);
        // 5xx and 429 are transient server-side failures worth retrying. Other
        // 4xx responses are a rejection (bad request / document rejected) that
        // an identical resend won't fix, so they go straight to terminal.
        const retryable = response.status >= 500 || response.status === 429;
        // Analytics: the server responded with an error status.
        this.trackEvent("vac_upload_submission_error", { error_type: "server" });
        this.handleUploadFailure(retryable);
        return;
      }

      this.setUploadState("success");
    } catch (err) {
      // Network failure, CORS rejection, DNS/TLS error, or our own timeout abort
      // all land here. All are transient and retryable — see the idempotency
      // caveat in the docs. AbortError is only ever our timeout (nothing else
      // aborts this request), so it is safe to treat as a stall.
      const timedOut = err && err.name === "AbortError";
      console.error(`[document-uploader] upload failed — ${timedOut ? `timed out after ${this._uploadTimeoutMs}ms` : "network/CORS error"}`, err);
      // Analytics: a stalled upload (our abort timer) is a timeout; anything else
      // reaching here is a network/CORS/DNS/TLS failure.
      this.trackEvent("vac_upload_submission_error", { error_type: timedOut ? "timeout" : "network" });
      this.handleUploadFailure(true);
    } finally {
      clearTimeout(timer);
    }
  }

  // Routes a failed submission to the right end state. A retryable failure with
  // budget remaining shows the "Try again" affordance; anything else (including
  // a retryable failure once the budget is spent) lands on the terminal
  // "contact Client Care" state.
  handleUploadFailure(retryable) {
    if (retryable && this.retryCount < this.maxRetries) {
      this.setUploadState("error-retry");
    } else {
      this.setUploadState("error-final");
    }
  }

  setUploadState(state) {
    const messages = {
      verifying: this.uploadState?.dataset.messageVerifying,
      success: this.uploadState?.dataset.messageSuccess,
      // Retryable failure keeps its own message + the "Try again" button; the
      // terminal failure reuses the existing "contact Client Care" copy.
      "error-retry": this.uploadState?.dataset.messageErrorRetry,
      "error-final": this.uploadState?.dataset.messageErrorVerifier,
    };

    const isError = state === "error-retry" || state === "error-final";

    if (this.uploadStateMessage && messages[state]) {
      this.uploadStateMessage.textContent = messages[state];
      // Only the error states are coloured red — verifying/success keep the default.
      this.uploadStateMessage.classList.toggle("document-uploader__panel-upload-state-message--error", isError);
    }

    // The retry affordance is shown only while a retry is still available.
    this.showRetryButton(state === "error-retry");

    // Move focus to the new error state so assistive tech lands on it (mirroring
    // the success path) rather than staying on the now-hidden submit button.
    // Retryable → the actionable "Try again" button (its aria-describedby reads
    // the error reason); terminal → the message itself (no button to catch focus;
    // it carries tabindex="-1" to be programmatically focusable).
    if (state === "error-retry" && this.retryButton) {
      this.retryButton.focus();
    } else if (state === "error-final" && this.uploadStateMessage) {
      this.uploadStateMessage.focus();
    }

    if (state === "success") this.showConfirmation();
  }

  // Toggles the "Try again" button. Hidden on every state except the retryable
  // error state so it never lingers into verifying/success/terminal.
  showRetryButton(show) {
    if (this.retryActions) this.retryActions.hidden = !show;
  }

  // Success screen: populate the submission summary from the submitted values,
  // then swap the progress UI for the "Document Received" confirmation.
  showConfirmation() {
    this.renderConfirmationSummary();

    // Analytics (conversion): the confirmation screen is rendering successfully.
    // Mark the flow complete so a subsequent page exit isn't logged as abandoned.
    this._completed = true;
    this.trackEvent("vac_upload_complete");

    if (this.statusPanel) this.statusPanel.classList.add("document-uploader__panel--complete");
    if (this.uploadState) this.uploadState.hidden = true;
    if (this.confirmation) this.confirmation.hidden = false;
    if (this.introMessage) this.introMessage.hidden = true;

    // Move focus to the success heading so assistive tech lands on the new state
    // (and reads it) rather than staying on the now-hidden submit button. The
    // heading carries tabindex="-1" to be programmatically focusable; letting
    // focus() scroll brings the confirmation into view.
    if (this.confirmationHeading) this.confirmationHeading.focus();
  }

  // Fills each [data-summary-field] with its submitted value. "file" resolves to
  // the processed upload's filename; every other key maps to a form field name.
  renderConfirmationSummary() {
    if (!this.confirmation) return;

    this.confirmation.querySelectorAll("[data-summary-field]").forEach((el) => {
      const key = el.dataset.summaryField;

      if (key === "file") {
        const file = this.getUploadFile();
        el.textContent = file ? file.name : "";
        return;
      }

      const field = this.form?.querySelector(`[name="${key}"]`);
      el.textContent = field ? field.value : "";
    });
  }

  showFormError(message) {
    if (!this.formError) return;
    this.formError.textContent = message;
    this.formError.removeAttribute("hidden");
  }

  clearFormError() {
    if (!this.formError) return;
    this.formError.textContent = "";
    this.formError.setAttribute("hidden", "");
  }

  setPanel(panelId) {
    this.querySelectorAll("[data-panel-id]").forEach((panel) => {
      panel.classList.toggle("hide", panel.dataset.panelId !== panelId);
    });
  }
}

if (!customElements.get("document-uploader")) {
  customElements.define("document-uploader", DocumentUploader);
}
